import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { MicIcon, PlayIcon, Volume2Icon, LanguagesIcon, PauseIcon } from 'lucide-react';
export function HybridAnnouncement() {
  const [language, setLanguage] = useState<'en' | 'ne'>('en');
  const [isPlaying, setIsPlaying] = useState(false);
  const announcements = {
    en: {
      name: 'Mr. Rajesh Kumar',
      dept: 'please proceed to the Loan Department.'
    },
    ne: {
      name: 'श्री राजेश कुमार',
      dept: 'कृपया ऋण विभागमा जानुहोस्।'
    }
  };
  // Auto-stop playback simulation
  useEffect(() => {
    if (isPlaying) {
      const timer = setTimeout(() => setIsPlaying(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [isPlaying]);
  const togglePlayback = () => setIsPlaying(!isPlaying);
  return <section id="announcement" className="py-24 bg-white relative overflow-hidden" data-id="element-167">
      {/* Background decoration */}
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-slate-200 to-transparent" data-id="element-168"></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-169">
        <div className="text-center max-w-3xl mx-auto mb-16" data-id="element-170">
          <h2 className="text-accent font-semibold tracking-wide uppercase text-sm mb-3" data-id="element-171">
            A Unique Feature
          </h2>
          <h3 className="text-3xl md:text-4xl font-bold text-heading mb-6" data-id="element-172">
            Hybrid Announcement System
          </h3>
          <p className="text-lg text-slate-600" data-id="element-173">
            Unlike conventional systems that announce only token numbers,
            ERPLogiFlow introduces an innovative hybrid approach. Staff record a
            customer's name live, which seamlessly stitches with professionally
            pre-recorded department names.
          </p>
        </div>

        <div className="max-w-4xl mx-auto" data-id="element-174">
          <div className="bg-slate-50 rounded-2xl border border-slate-200 p-6 md:p-10 shadow-sm" data-id="element-175">
            {/* Controls Header */}
            <div className="flex flex-col sm:flex-row justify-between items-center gap-6 mb-10 pb-6 border-b border-slate-200" data-id="element-176">
              <div className="flex items-center gap-3" data-id="element-177">
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary" data-id="element-178">
                  <Volume2Icon className="w-5 h-5" data-id="element-179" />
                </div>
                <div data-id="element-180">
                  <h4 className="font-semibold text-heading" data-id="element-181">Live Preview</h4>
                  <p className="text-sm text-slate-500" data-id="element-182">
                    Listen to the stitched announcement
                  </p>
                </div>
              </div>

              {/* Language Toggle */}
              <div className="flex items-center bg-white border border-slate-200 rounded-lg p-1 shadow-sm" data-id="element-183">
                <button onClick={() => setLanguage('en')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${language === 'en' ? 'bg-primary text-white shadow' : 'text-slate-600 hover:text-primary'}`} data-id="element-184">
                  English
                </button>
                <button onClick={() => setLanguage('ne')} className={`px-4 py-2 rounded-md text-sm font-medium transition-all ${language === 'ne' ? 'bg-primary text-white shadow' : 'text-slate-600 hover:text-primary'}`} data-id="element-185">
                  नेपाली
                </button>
              </div>
            </div>

            {/* Visualizer Area */}
            <div className="flex flex-col md:flex-row gap-6 items-stretch mb-8" data-id="element-186">
              {/* Live Recorded Segment */}
              <div className="flex-1 bg-white rounded-xl border border-slate-200 p-6 relative overflow-hidden group" data-id="element-187">
                <div className="absolute top-0 left-0 w-1 h-full bg-accent" data-id="element-188"></div>
                <div className="flex justify-between items-start mb-4" data-id="element-189">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1" data-id="element-190">
                    <MicIcon className="w-3 h-3" data-id="element-191" /> Live Recording
                  </span>
                  <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded" data-id="element-192">
                    Staff Input
                  </span>
                </div>
                <p className="text-xl font-medium text-heading mb-6" data-id="element-193">
                  "{announcements[language].name}"
                </p>
                {/* Simulated Waveform */}
                <div className="flex items-end gap-1 h-12 opacity-60" data-id="element-194">
                  {[...Array(20)].map((_, i) => <motion.div key={`live-${i}`} className="w-full bg-accent rounded-t-sm" initial={{
                  height: '20%'
                }} animate={{
                  height: isPlaying ? `${Math.random() * 80 + 20}%` : '20%'
                }} transition={{
                  repeat: isPlaying ? Infinity : 0,
                  duration: 0.2,
                  repeatType: 'reverse',
                  delay: i * 0.05
                }} data-id="element-195" />)}
                </div>
              </div>

              {/* Plus Icon */}
              <div className="flex items-center justify-center text-slate-300" data-id="element-196">
                <div className="w-8 h-8 rounded-full bg-white border border-slate-200 flex items-center justify-center shadow-sm" data-id="element-197">
                  <span className="font-bold text-lg" data-id="element-198">+</span>
                </div>
              </div>

              {/* Pre-recorded Segment */}
              <div className="flex-1 bg-white rounded-xl border border-slate-200 p-6 relative overflow-hidden" data-id="element-199">
                <div className="absolute top-0 left-0 w-1 h-full bg-primary" data-id="element-200"></div>
                <div className="flex justify-between items-start mb-4" data-id="element-201">
                  <span className="text-xs font-bold uppercase tracking-wider text-slate-400 flex items-center gap-1" data-id="element-202">
                    <Volume2Icon className="w-3 h-3" data-id="element-203" /> Pre-recorded
                  </span>
                  <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2 py-1 rounded" data-id="element-204">
                    System Audio
                  </span>
                </div>
                <p className="text-xl font-medium text-heading mb-6" data-id="element-205">
                  {language === 'en' ? ', ' : ' '}"
                  {announcements[language].dept}"
                </p>
                {/* Simulated Waveform */}
                <div className="flex items-end gap-1 h-12 opacity-60" data-id="element-206">
                  {[...Array(20)].map((_, i) => <motion.div key={`pre-${i}`} className="w-full bg-primary rounded-t-sm" initial={{
                  height: '20%'
                }} animate={{
                  height: isPlaying ? `${Math.random() * 80 + 20}%` : '20%'
                }} transition={{
                  repeat: isPlaying ? Infinity : 0,
                  duration: 0.2,
                  repeatType: 'reverse',
                  delay: isPlaying ? i * 0.05 + 1 : 0 // Delay to simulate sequential playing
                }} data-id="element-207" />)}
                </div>
              </div>
            </div>

            {/* Play Button & Final Output */}
            <div className="bg-primary rounded-xl p-6 text-white flex flex-col sm:flex-row items-center gap-6 shadow-lg" data-id="element-208">
              <button onClick={togglePlayback} className="w-14 h-14 rounded-full bg-accent hover:bg-accent-hover text-white flex items-center justify-center flex-shrink-0 transition-colors shadow-md" aria-label={isPlaying ? 'Pause announcement' : 'Play announcement'} data-id="element-209">
                {isPlaying ? <PauseIcon className="w-6 h-6" data-id="element-210" /> : <PlayIcon className="w-6 h-6 ml-1" data-id="element-211" />}
              </button>
              <div data-id="element-212">
                <div className="text-primary-light text-xs font-bold uppercase tracking-wider mb-1" data-id="element-213">
                  Final Output
                </div>
                <p className="text-lg sm:text-xl font-medium leading-snug" data-id="element-214">
                  "{announcements[language].name}
                  {language === 'en' ? ', ' : ' '}
                  {announcements[language].dept}"
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}